import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import api from '../api/client.js';
import ExportButton from '../components/ExportButton.jsx';
import CustomerSearchSelect from '../components/CustomerSearchSelect.jsx';
import { useAuth } from '../auth/AuthContext.jsx';

const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];
const PAGE_SIZE = 50;

const TOUCH_TYPES = [
  { value: '',      label: 'All types' },
  { value: 'note',  label: 'Note' },
  { value: 'visit', label: 'Visit' },
  { value: 'call',  label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'quote', label: 'Quote' },
];
const STATUSES = [
  { value: '',         label: 'All statuses' },
  { value: 'new',      label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'archived', label: 'Archived' },
];

const TOUCH_ICON = { note: '📝', visit: '🤝', call: '📞', email: '✉️', quote: '🧾', photo: '📷' };
const URGENCY_DOT = { high: 'text-crimson', medium: 'text-gold', low: 'text-muted' };

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(String(dateStr).length === 10 ? dateStr + 'T12:00:00' : dateStr)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Notes() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [activeTab, setActiveTab] = useState('recent');
  const qc = useQueryClient();

  // Inline "change customer" reassignment, available from both tabs. Shared
  // across tabs since only one row can be edited at a time.
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [localCustomer, setLocalCustomer] = useState(null); // { id, name, hint } while the picker is open
  const linkCustomer = useMutation({
    mutationFn: ({ noteId, customerId, nameHint }) =>
      api.post(`/notes/${noteId}/link-customer`,
        nameHint ? { customer_name_hint: nameHint } : { customer_id: customerId }
      ).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notes'] });
      setEditingNoteId(null);
      setLocalCustomer(null);
    },
  });
  function startEditingCustomer(e, noteId, currentCustomerId, currentCustomerName, currentHint) {
    e.preventDefault();
    e.stopPropagation();
    setLocalCustomer({ id: currentCustomerId || null, name: currentCustomerName || '', hint: currentCustomerId ? '' : (currentHint || '') });
    setEditingNoteId(noteId);
  }
  function cancelEditingCustomer(e) {
    e.preventDefault();
    e.stopPropagation();
    setEditingNoteId(null);
    setLocalCustomer(null);
  }

  // ── Recent tab state ──────────────────────────────────────────────────
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [touchType, setTouchType] = useState('');
  const [status, setStatus] = useState('');
  const [highAlert, setHighAlert] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [userId, setUserId] = useState('');
  const [offset, setOffset] = useState(0);

  const onFilter = (setter) => (v) => { setter(v); setOffset(0); };

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const reps = useQuery({
    queryKey: ['notes', 'reps'],
    queryFn: () => api.get('/notes/reps').then(r => r.data.data),
    enabled: isAdmin,
    staleTime: 5 * 60_000,
  });

  const buildParams = (extra = {}) => {
    const p = new URLSearchParams();
    if (debouncedQ) p.set('q', debouncedQ);
    if (touchType) p.set('touch_type', touchType);
    if (status) p.set('status', status);
    if (highAlert) p.set('high_alert', '1');
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (isAdmin && userId) p.set('user_id', userId);
    for (const [k, v] of Object.entries(extra)) p.set(k, v);
    return p;
  };

  const list = useQuery({
    queryKey: ['notes', 'list', debouncedQ, touchType, status, highAlert, from, to, userId, offset],
    queryFn: () => {
      const p = buildParams({ limit: PAGE_SIZE, offset });
      return api.get(`/notes?${p}`).then(r => r.data);
    },
    placeholderData: keepPreviousData,
    enabled: activeTab === 'recent',
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const hasActiveFilters = debouncedQ || touchType || status || highAlert || from || to || (isAdmin && userId);

  const clearFilters = () => {
    setQ(''); setDebouncedQ(''); setTouchType(''); setStatus('');
    setHighAlert(false); setFrom(''); setTo(''); setUserId(''); setOffset(0);
  };

  // ── By Customer tab state ─────────────────────────────────────────────
  const [customerFilter, setCustomerFilter] = useState('');

  const timeline = useQuery({
    queryKey: ['notes', 'customer-timeline'],
    queryFn: () => api.get('/notes/customer-timeline').then(r => r.data.data),
    enabled: activeTab === 'by-customer',
    staleTime: 60_000,
  });

  const customerOptions = useMemo(() => {
    if (!timeline.data) return [];
    return timeline.data.map(c => ({ id: c.customer_id, name: c.customer_name }));
  }, [timeline.data]);

  const displayedCustomers = useMemo(() => {
    if (!timeline.data) return [];
    if (!customerFilter) return timeline.data;
    const id = parseInt(customerFilter, 10);
    return timeline.data.filter(c => c.customer_id === id);
  }, [timeline.data, customerFilter]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="section-eyebrow">Activity</div>
          <h1 className="text-2xl">All Notes</h1>
          <div className="rule-crimson" />
        </div>
        <div className="flex gap-2 items-center">
          {activeTab === 'recent' && <ExportButton path={`/export/notes.csv?${buildParams()}`} />}
          <Link to="/notes/new" className="btn-primary">🎙 New Note</Link>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-navy-100 pb-0">
        {[
          { id: 'recent', label: 'Recent' },
          { id: 'by-customer', label: 'By Customer' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition',
              activeTab === tab.id
                ? 'border-crimson text-navy'
                : 'border-transparent text-muted hover:text-navy'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── RECENT TAB ───────────────────────────────────────────────────── */}
      {activeTab === 'recent' && (
        <>
          <div className="card p-3 flex flex-wrap gap-2 items-center">
            <input
              className="input text-sm flex-1 min-w-[12rem]"
              placeholder="Search transcript, summary, contact, customer…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            {isAdmin && (
              <select className="input text-sm" value={userId}
                      onChange={e => onFilter(setUserId)(e.target.value)}>
                <option value="">All reps</option>
                {(reps.data ?? []).map(r => (
                  <option key={r.id} value={r.id}>{r.full_name}</option>
                ))}
              </select>
            )}
            <select className="input text-sm" value={touchType}
                    onChange={e => onFilter(setTouchType)(e.target.value)}>
              {TOUCH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select className="input text-sm" value={status}
                    onChange={e => onFilter(setStatus)(e.target.value)}>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <label className="text-sm flex items-center gap-1.5 text-navy">
              <span className="text-muted">From</span>
              <input type="date" className="input text-sm" value={from}
                     onChange={e => onFilter(setFrom)(e.target.value)} />
            </label>
            <label className="text-sm flex items-center gap-1.5 text-navy">
              <span className="text-muted">To</span>
              <input type="date" className="input text-sm" value={to}
                     onChange={e => onFilter(setTo)(e.target.value)} />
            </label>
            <button
              onClick={() => onFilter(setHighAlert)(!highAlert)}
              className={clsx('btn px-3 py-1 text-sm',
                highAlert ? 'bg-crimson text-white' : 'bg-panel text-navy')}>
              ⚠ High alert
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="btn-ghost text-sm">Clear</button>
            )}
          </div>

          <div className="flex items-center justify-between text-sm text-muted">
            <span>
              {list.isLoading ? 'Loading…'
                : total === 0 ? 'No notes match these filters.'
                : `Showing ${pageStart}–${pageEnd} of ${total}`}
            </span>
            {total > PAGE_SIZE && (
              <div className="flex gap-2 items-center">
                <button className="btn-ghost text-sm" disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>‹ Prev</button>
                <button className="btn-ghost text-sm" disabled={pageEnd >= total}
                        onClick={() => setOffset(offset + PAGE_SIZE)}>Next ›</button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            {!list.isLoading && rows.length === 0 && (
              <div className="card p-4 text-muted text-sm">
                {hasActiveFilters ? 'No notes match these filters.' : 'No notes yet.'}
              </div>
            )}
            {rows.map(n => {
              const s = n.structured || {};
              const highAlertNote = s.high_alert === true;
              const isEditingCustomer = editingNoteId === n.id;
              return (
                <Link key={n.id} to={`/notes/${n.id}`}
                  className={clsx('card p-3 block hover:bg-panel border-l-4',
                    highAlertNote ? 'border-crimson' : 'border-navy-200')}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-navy flex items-center gap-2 min-w-0 flex-1">
                      <span title={n.touch_type || 'note'}>{TOUCH_ICON[n.touch_type] || '📝'}</span>
                      {isEditingCustomer ? (
                        <div className="flex-1 min-w-0 max-w-xs flex items-center gap-1" onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
                          <div className="flex-1 min-w-0">
                            <CustomerSearchSelect
                              value={localCustomer?.id}
                              valueLabel={localCustomer?.name}
                              hintValue={localCustomer?.hint}
                              suggestRecent
                              onChange={(cid, name, isHint) => {
                                if (isHint) linkCustomer.mutate({ noteId: n.id, nameHint: name });
                                else if (cid) linkCustomer.mutate({ noteId: n.id, customerId: cid });
                                else setLocalCustomer({ id: null, name: '', hint: '' });
                              }}
                            />
                          </div>
                          <button type="button" className="text-xs text-muted hover:underline" onClick={cancelEditingCustomer}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          {n.customer_name
                            ? <span>{n.customer_name} <span className="inline-flex items-center rounded px-1 py-0.5 bg-emerald-100" title="Linked">🔗</span></span>
                            : (n.customer_name_hint || s.customer_name || s.customer_name_guess)
                              ? <span>{n.customer_name_hint || s.customer_name || s.customer_name_guess} <span className="inline-flex items-center rounded px-1 py-0.5 bg-amber-100" title="Unlinked">🔗</span></span>
                              : <span>(no customer)</span>}
                          <button
                            type="button"
                            className="text-navy/40 hover:text-navy text-xs"
                            title="Change customer"
                            onClick={e => startEditingCustomer(e, n.id, n.customer_id, n.customer_name, n.customer_name_hint)}
                          >
                            ✏️
                          </button>
                        </>
                      )}
                      {n.contact_name && <span className="text-xs text-muted">· {n.contact_name}</span>}
                    </div>
                    <div className="text-xs text-muted whitespace-nowrap text-right">
                      <div>{new Date(n.created_at).toLocaleString()}</div>
                      {isAdmin && n.user_name && (
                        <div className="text-navy font-medium">✍ {n.user_name}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-ink mt-1 line-clamp-2">
                    {s.summary || n.transcript?.slice(0, 160) || '(processing…)'}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {highAlertNote && (
                      <span className="badge text-[10px] font-semibold uppercase tracking-wider text-crimson bg-red-100 px-1.5 py-0.5 rounded">
                        ⚠ High alert
                      </span>
                    )}
                    {n.commitments_open && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gold bg-amber-100 px-1.5 py-0.5 rounded">
                        Open commitments
                      </span>
                    )}
                    {n.open_followups > 0 && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-navy bg-panel px-1.5 py-0.5 rounded">
                        {n.open_followups} follow-up{n.open_followups === 1 ? '' : 's'}
                      </span>
                    )}
                    {n.follow_up_id && (
                      <span className="text-[10px] uppercase tracking-wider text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded">📋 follow-up note</span>
                    )}
                    {n.status && n.status !== 'new' && (
                      <span className="text-[10px] uppercase tracking-wider text-muted">{n.status}</span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-muted">
              <span>Showing {pageStart}–{pageEnd} of {total}</span>
              <div className="flex gap-2 items-center">
                <button className="btn-ghost text-sm" disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>‹ Prev</button>
                <button className="btn-ghost text-sm" disabled={pageEnd >= total}
                        onClick={() => setOffset(offset + PAGE_SIZE)}>Next ›</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── BY CUSTOMER TAB ──────────────────────────────────────────────── */}
      {activeTab === 'by-customer' && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              className="input text-sm flex-1 max-w-xs"
              value={customerFilter}
              onChange={e => setCustomerFilter(e.target.value)}
            >
              <option value="">All customers</option>
              {customerOptions.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {customerFilter && (
              <button className="btn-ghost text-sm" onClick={() => setCustomerFilter('')}>Show all</button>
            )}
            {timeline.data && (
              <span className="text-sm text-muted">
                {customerFilter
                  ? `1 of ${customerOptions.length} customers`
                  : `${customerOptions.length} customer${customerOptions.length === 1 ? '' : 's'}`}
              </span>
            )}
          </div>

          {timeline.isLoading && <div className="text-muted text-sm">Loading…</div>}
          {timeline.isError && <div className="text-crimson text-sm">Failed to load timeline.</div>}

          {!timeline.isLoading && displayedCustomers.length === 0 && (
            <div className="card p-4 text-muted text-sm">No notes linked to customers yet.</div>
          )}

          <div className="space-y-4">
            {displayedCustomers.map(customer => (
              <div key={customer.customer_id} className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <Link to={`/customers/${customer.customer_id}`}
                    className="font-semibold text-navy hover:text-crimson hover:underline">
                    {customer.customer_name}
                  </Link>
                  <span className="text-xs text-muted">
                    {customer.notes.length} note{customer.notes.length === 1 ? '' : 's'}
                  </span>
                </div>

                <div className="space-y-2">
                  {customer.notes.map(note => {
                    const isEditingCustomer = editingNoteId === note.id;
                    return (
                    <div key={note.id}>
                      {/* Root note */}
                      <div className="flex items-start gap-2 px-2 py-2 rounded hover:bg-panel border-l-2 border-navy-200 group">
                        <Link to={`/notes/${note.id}`} className="flex items-start gap-2 flex-1 min-w-0">
                          <span className="text-base mt-0.5">{TOUCH_ICON[note.touch_type] || '📝'}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-navy line-clamp-2">
                              {note.summary || '(processing…)'}
                            </div>
                            <div className="text-xs text-muted mt-0.5">{fmt(note.note_date || note.created_at)}</div>
                          </div>
                        </Link>
                        <button
                          type="button"
                          className="text-navy/40 hover:text-navy text-xs mt-0.5 shrink-0"
                          title="Change customer"
                          onClick={e => startEditingCustomer(e, note.id, customer.customer_id, customer.customer_name)}
                        >
                          ✏️
                        </button>
                      </div>
                      {isEditingCustomer && (
                        <div className="ml-7 mt-1 max-w-xs flex items-center gap-1">
                          <div className="flex-1 min-w-0">
                            <CustomerSearchSelect
                              value={localCustomer?.id}
                              valueLabel={localCustomer?.name}
                              hintValue={localCustomer?.hint}
                              suggestRecent
                              onChange={(cid, name, isHint) => {
                                if (isHint) linkCustomer.mutate({ noteId: note.id, nameHint: name });
                                else if (cid) linkCustomer.mutate({ noteId: note.id, customerId: cid });
                                else setLocalCustomer({ id: null, name: '', hint: '' });
                              }}
                            />
                          </div>
                          <button type="button" className="text-xs text-muted hover:underline" onClick={cancelEditingCustomer}>Cancel</button>
                        </div>
                      )}

                      {/* Follow-up chain */}
                      {note.followups.map(fu => (
                        <div key={fu.id} className="ml-5 mt-1">
                          {/* Follow-up connector */}
                          <div className="flex items-start gap-1 px-2 py-1">
                            <span className="text-muted text-xs mt-0.5 select-none">↓</span>
                            <div className="flex-1 min-w-0">
                              <Link to={`/followups/${fu.id}`}
                                className="inline-flex items-center gap-1 text-xs text-navy hover:underline">
                                <span className="font-medium">{fu.title}</span>
                                <span className={clsx('capitalize text-[10px]', URGENCY_DOT[fu.urgency] || 'text-muted')}>
                                  · {fu.urgency}
                                </span>
                                <span className={clsx('text-[10px]',
                                  fu.status === 'done' ? 'text-emerald-600' : 'text-muted')}>
                                  · {fu.status}
                                </span>
                                <span className="text-[10px] text-muted">· due {fmt(fu.due_date)}</span>
                              </Link>
                            </div>
                          </div>

                          {/* Note for this follow-up */}
                          {fu.reply_note && (
                            <div className="ml-4">
                              <Link to={`/notes/${fu.reply_note.id}`}
                                className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-panel border-l-2 border-sky-300">
                                <span className="text-sm mt-0.5">📋</span>
                                <span className="text-sm mt-0.5">{TOUCH_ICON[fu.reply_note.touch_type] || '📝'}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-navy line-clamp-2">
                                    {fu.reply_note.summary || '(note)'}
                                  </div>
                                  <div className="text-xs text-muted mt-0.5">
                                    {fmt(fu.reply_note.note_date || fu.reply_note.created_at)}
                                  </div>
                                </div>
                              </Link>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
